import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatchItem, ClassroomItem, DepartmentItem, GetStudentsRequest, StudentItem } from '@edtech-ui/core/models/Classroom/get-all-students';
import { StudentManagementService } from '@edtech-ui/core/services/admin-panel/studentManagement.service';
import { TeacherManagementService } from '@edtech-ui/core/services/admin-panel/teacherManagement.service';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { PaginatorModule } from 'primeng/paginator';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { BatchManagementService } from '@edtech-ui/core/services/batch-management/batch-management.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-allstudents',
  standalone: true,
  imports: [FormsModule, CommonModule, PaginatorModule],
  templateUrl: './allstudents.component.html',
  styleUrl: './allstudents.component.scss'
})
export class AllstudentsComponent {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // UI
  searchText = '';
  isLoading = false;

  // Filters
  selectedDepartment = 0;
  selectedBatch = 0;
  selectedClassroom = 0;

  // Pagination
  page = 1;
  limit = 10;
  totalRecords = 0;

  // Data
  departments: DepartmentItem[] = [];
  batches: BatchItem[] = [];
  classrooms: ClassroomItem[] = [];
  students: StudentItem[] = [];

  // Maps
  departmentNames: Record<number, string> = {};
  batchNames: Record<number, string> = {};
  classroomNames: Record<number, string> = {};

  constructor(
    private teacherService: TeacherManagementService,
    private batchService: BatchManagementService,
    private classroomService: ClassroomManagementService,
    private studentService: StudentManagementService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadDepartments();
    this.loadBatches();
    this.loadClassrooms();

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.page = 1;
        this.loadStudents();
      });

    this.loadStudents();
  }


  loadDepartments() {
    this.teacherService.getDepartments()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.Success) {
            const list = [
              { id: 0 as any, name: 'All Departments' },
              ...res.Data.map(d => ({
                id: Number(d.id),
                name: d.name
              }))
            ];
            this.departments = list;
            this.setDepartmentMap(list as any);
          }
        },
        error: () => this.toastr.error('Failed to load departments')
      });
  }

  loadBatches() {
    this.batchService.getAllTenantsBatches()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.Success) {
            const list = [
              { id: 0 as any, name: 'All Batches' },
              ...res.Data.batches.map(b => ({
                id: Number(b.id),
                name: b.name
              }))
            ];
            this.batches = list;
            this.setBatchMap(list as any);
          }
        },
        error: () => this.toastr.error('Failed to load batches')
      });
  }

  loadClassrooms() {
    this.classroomService.getAllTeacherClassrooms('all', 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.Success) {
            const list = [
              { id: 0 as any, name: 'All Classrooms' },
              ...res.Data.classrooms.map(c => ({
                id: Number(c.id),
                name: c.name
              }))
            ];
            this.classrooms = list;
            this.setClassroomMap(list as any);
          }
        },
        error: () => this.toastr.error('Failed to load classrooms')
      });
  }

  loadStudents() {
    this.isLoading = true;

    const request: GetStudentsRequest = {
      page: this.page,
      page_size: this.limit,
      search: this.searchText || '',
      classroom_filter: this.selectedClassroom === 0 ? null : this.selectedClassroom,
      department_filter: this.selectedDepartment === 0 ? null : this.selectedDepartment,
      batch_filter: this.selectedBatch === 0 ? null : this.selectedBatch
    };

    this.classroomService.getAllStudents(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.isLoading = false;

          if (res.Success) {
            this.students = res.Data.students ?? [];
            this.totalRecords = res.Data.total_count ?? 0;

            if (this.students.length === 0) {
              this.toastr.info('No students found for selected filters');
            }

          } else {
            this.students = [];
            this.totalRecords = 0;
          }
        },
        error: () => {
          this.isLoading = false;
          this.toastr.error('Failed to load students');
        }
      });
  }

  searchStudent(value: string) {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  applyFilter() {
    this.page = 1;
    this.loadStudents();
  }

  clearDepartment() {
    this.selectedDepartment = 0;
    this.applyFilter();
  }

  clearBatch() {
    this.selectedBatch = 0;
    this.applyFilter();
  }

  clearClassroom() {
    this.selectedClassroom = 0;
    this.applyFilter();
  }

  onPageChange(event: any) {
    this.page = event.page + 1;
    this.limit = event.rows;
    this.loadStudents();
  }

  setDepartmentMap(items: DepartmentItem[]) {
    this.departmentNames = {};
    items.forEach(it => this.departmentNames[Number(it.id)] = it.name);
  }

  setBatchMap(items: BatchItem[]) {
    this.batchNames = {};
    items.forEach(it => this.batchNames[Number(it.id)] = it.name);
  }

  setClassroomMap(items: ClassroomItem[]) {
    this.classroomNames = {};
    items.forEach(it => this.classroomNames[Number(it.id)] = it.name);
  }

  getClassroomName(id: number) {
    return this.classroomNames[id] ?? '-';
  }

  getDepartmentName(id: number) {
    return this.departmentNames[id] ?? '-';
  }

  getBatchName(id: number) {
    return this.batchNames[id] ?? '-';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}